import { type JSX, useEffect, useState } from 'react';

import { ExportsApi } from 'Api/Exports/ExportsApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { DataGrid, GridColumn, dateColumn } from 'Components/DataGrid/DataGrid';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { EXPORTS } from 'Config/Paths';
import { controlComparator } from 'Helpers/Compare';
import { getHumanReadableControlIdFromControl } from 'Helpers/ControlFormatter/ControlFormatter';
import { toTitleCase } from 'Helpers/StringUtils';
import { getDepartmentFromSubject, getUserNameFromSubject } from 'Helpers/UserUtils';
import { ExceptionExportResponse } from 'Models/Exceptions';
import { UploadedFile } from 'Models/Files';
import { OperationalControl } from 'Models/OperationalControls';

interface ExceptionExportRow extends ExceptionExportResponse {
    owner_department?: never; // This does not come from the back-end. This is a column in the DataGrid that is derived from the owner's user ID.
}

export interface ExceptionsExportProps {
    exportsApi: ExportsApi;
    disableVirtualization?: boolean;
}

export const ExceptionsExport = (props: ExceptionsExportProps): JSX.Element => {
    const [exceptions, setExceptions] = useState<ExceptionExportResponse[]>();
    const [error, setError] = useState<string>();
    const cachedData = useCachedData();

    // Save the Tags map locally so it can be repeatedly accessed rather than recreating it as the rows and Tags are looped through.
    const tagsMap = cachedData.getTagsMap();

    useEffect(() => {
        const getExceptions = async (): Promise<void> => {
            try {
                const exceptionResponse = await props.exportsApi.getExceptionsExport();
                setExceptions(exceptionResponse.data);
            } catch (error) {
                setError(error.message);
            }
        };

        getExceptions();
    }, [props.exportsApi]);

    const columns: GridColumn<ExceptionExportRow>[] = [
        { field: 'title', headerName: 'Title', width: 300 },
        { field: 'owner_subject', headerName: 'Owner', width: 300, valueGetter: (value) => getUserNameFromSubject(value, cachedData.users) },
        { field: 'owner_department', headerName: 'Owner Department', width: 300, valueGetter: (_value, row) => getDepartmentFromSubject(row.owner_subject, cachedData.users) },
        { field: 'reviewer_subject', headerName: 'Reviewer', width: 300, valueGetter: (value) => getUserNameFromSubject(value, cachedData.users) },
        {
            field: 'delegates',
            headerName: 'Delegates',
            width: 300,
            valueGetter: (value) =>
                value
                    .map((userId: string) => getUserNameFromSubject(userId, cachedData.users))
                    .sort()
                    .join(', '),
        },
        { field: 'status', headerName: 'Status', width: 300, valueGetter: (value) => toTitleCase(value) },
        { field: 'likelihood', headerName: 'Likelihood', width: 300, valueGetter: (value) => toTitleCase(value) },
        { field: 'impact', headerName: 'Impact', width: 300, valueGetter: (value) => toTitleCase(value, '/') },
        { field: 'risk_score', headerName: 'Risk Score', width: 300, valueGetter: (value) => toTitleCase(value, '/') },
        { field: 'reference', headerName: 'Reference', width: 300 },
        { field: 'description', headerName: 'Description', width: 300 },
        { field: 'risk_assessment', headerName: 'Risk Description', width: 300 },
        { field: 'remediation_plan', headerName: 'Remediation Plan', width: 300 },
        { field: 'compensating_controls', headerName: 'Compensating Controls', width: 300 },
        {
            field: 'impacted_controls',
            headerName: 'Impacted Controls',
            width: 300,
            valueGetter: (_value, row) =>
                row.impacted_controls
                    .sort(controlComparator)
                    .map((control: OperationalControl) => getHumanReadableControlIdFromControl(control))
                    .join(', '),
        },
        { field: 'impacted_vendor', headerName: 'Impacted Third Party', width: 300 },
        dateColumn('created_timestamp', 'Open Date'),
        { field: 'created_by', headerName: 'Created By', width: 300, valueGetter: (value) => getUserNameFromSubject(value, cachedData.users) },
        dateColumn('last_updated_timestamp', 'Last Updated'),
        { field: 'last_updated_by', headerName: 'Last Updated By', width: 300, valueGetter: (value) => getUserNameFromSubject(value, cachedData.users) },
        dateColumn('expiration_date', 'Expiration Date'),
        dateColumn('closed_timestamp', 'Closure Date'),
        { field: 'closed_by', headerName: 'Closed By', width: 300, valueGetter: (value) => getUserNameFromSubject(value, cachedData.users) },
        { field: 'closure_statement', headerName: 'Closure Statement', width: 300 },
        {
            field: 'files',
            headerName: 'File Names',
            width: 300,
            valueGetter: (_value, row) => row.files.map((file?: UploadedFile) => file?.filename).join(', '),
        },
        {
            field: 'tags',
            headerName: 'Tags',
            width: 300,
            valueGetter: (_value, row) =>
                row.tags
                    .sort()
                    .map((tagId: string) => tagsMap.get(tagId)?.title ?? tagId)
                    .join(', '),
        },
        { field: 'id', headerName: 'ID', width: 300 },
    ];

    if (exceptions) {
        return (
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={`/${EXPORTS}`}>Exports</BreadcrumbLink>
                        <BreadcrumbText>Exceptions</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle="Exceptions Export"
                body={[{ content: <DataGrid columns={columns} getRowId={(exception: ExceptionExportRow) => exception.id} rows={exceptions} title="Exceptions Export Preview" fileName="Exceptions" disableVirtualization={props.disableVirtualization} /> }]}
            />
        );
    } else if (error) {
        return <Text color="darkBlue">{error}</Text>;
    } else return <Placeholder />;
};
